import { getCloudApi } from '@/castapi/helpers';
import type { ISearchParams } from '@/castapi/helpers';
import { getErrorMessage } from '@/castapi/helpers';
import { AppLogger } from '@/logger';
import type {
  ChangeExpiryDateDto,
  CloudProductInfoDto,
  CloudProductSearchResponseDto,
  ComputerDto,
  CpOnlineCancelForceRequestDto,
  CpResetOnlineCancelForceRequestDto,
  GetCloudActivitiesResponseDto,
} from '@/castapi';
import type { IActionParams } from '@/store/modules/index';
import type { ITableParamsState } from '@/store/modules/common/tableParamsState';
import TableParamsState from '@/store/modules/common/tableParamsState';

const logger = new AppLogger('adminCloudProducts state');
const tableParams = new TableParamsState();

interface IAdminCloudProductsState extends ITableParamsState {
  currentCloudProduct?: CloudProductInfoDto;
  cloudProducts: CloudProductSearchResponseDto[];
  cloudProductsLoading: boolean;
  cloudProductsLoadError?: string;
  assignInProgress: boolean;
  assignError?: string;
  changeNotesInProgress: boolean;
  changeNotesError?: string;
  changeExpiryDateInProgress: boolean;
  changeExpiryDateError?: string;
  organizationAndUserCloudProductsLoading: boolean;
  organizationAndUserCloudProductsLoadError?: string;
  userCloudProductsLoading: boolean;
  userCloudProductsLoadError?: string;
  organizationAndUserCloudProducts: CloudProductInfoDto[];
  computers: ComputerDto[];
  computersLoading: boolean;
  computersLoadError?: string;
  cloudActivities: GetCloudActivitiesResponseDto[];
  cloudActivitiesLoading: boolean;
  cloudActivitiesLoadError?: string;
  forcedRevocationError?: string;
  forcedRevocationProgress: boolean;
  resetForcedRevocationError?: string;
  resetForcedRevocationProgress: boolean;
  replaceStuckEntitlementInProgress: boolean;
  replaceStuckEntitlementError?: string;
}

const initialState = (): IAdminCloudProductsState => ({
  ...tableParams.state(),
  currentCloudProduct: undefined,
  cloudProducts: [],
  cloudProductsLoading: false,
  cloudProductsLoadError: undefined,
  assignInProgress: false,
  assignError: undefined,
  changeNotesInProgress: false,
  changeNotesError: undefined,
  changeExpiryDateInProgress: false,
  changeExpiryDateError: undefined,
  organizationAndUserCloudProductsLoading: false,
  organizationAndUserCloudProductsLoadError: undefined,
  userCloudProductsLoading: false,
  userCloudProductsLoadError: undefined,
  organizationAndUserCloudProducts: [],
  computers: [],
  computersLoading: false,
  computersLoadError: undefined,
  cloudActivities: [],
  cloudActivitiesLoading: false,
  cloudActivitiesLoadError: undefined,
  forcedRevocationError: undefined,
  forcedRevocationProgress: false,
  resetForcedRevocationError: undefined,
  resetForcedRevocationProgress: false,
  replaceStuckEntitlementInProgress: false,
  replaceStuckEntitlementError: undefined,
});

type ActionParams = IActionParams<IAdminCloudProductsState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    ...tableParams.mutations,

    SELECT_CLOUD_PRODUCT(state: IAdminCloudProductsState, cloudProduct: CloudProductInfoDto): void {
      state.currentCloudProduct = cloudProduct;
    },

    CLOUD_PRODUCTS_LOADING(state: IAdminCloudProductsState): void {
      state.cloudProductsLoadError = undefined;
      state.cloudProductsLoading = true;
    },

    CLOUD_PRODUCTS_LOADED(state: IAdminCloudProductsState, payload: CloudProductSearchResponseDto[]): void {
      state.cloudProducts = payload;
      state.cloudProductsLoading = false;
    },

    CLOUD_PRODUCTS_LOAD_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.cloudProductsLoadError = getErrorMessage(error);
      state.cloudProductsLoading = false;
    },

    CLOUD_PRODUCT_ASSIGN_IN_PROGRESS(state: IAdminCloudProductsState): void {
      state.assignError = undefined;
      state.assignInProgress = true;
    },

    CLOUD_PRODUCT_ASSIGN_SUCCESS(state: IAdminCloudProductsState): void {
      state.assignError = undefined;
      state.assignInProgress = false;
    },

    CLOUD_PRODUCT_ASSIGN_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.assignError = getErrorMessage(error);
      state.assignInProgress = false;
    },

    CLOUD_PRODUCT_CHANGE_NOTES_IN_PROGRESS(state: IAdminCloudProductsState): void {
      state.changeNotesError = undefined;
      state.changeNotesInProgress = true;
    },

    CLOUD_PRODUCT_CHANGE_NOTES_SUCCESS(state: IAdminCloudProductsState): void {
      state.changeNotesError = undefined;
      state.changeNotesInProgress = false;
    },

    CLOUD_PRODUCT_CHANGE_NOTES_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.changeNotesError = getErrorMessage(error);
      state.changeNotesInProgress = false;
    },

    CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_RESET_STATE(state: IAdminCloudProductsState): void {
      state.changeExpiryDateError = undefined;
      state.changeExpiryDateInProgress = false;
    },

    CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_IN_PROGRESS(state: IAdminCloudProductsState): void {
      state.changeExpiryDateError = undefined;
      state.changeExpiryDateInProgress = true;
    },

    CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_SUCCESS(state: IAdminCloudProductsState): void {
      state.changeExpiryDateError = undefined;
      state.changeExpiryDateInProgress = false;
    },

    CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.changeExpiryDateError = getErrorMessage(error);
      state.changeExpiryDateInProgress = false;
    },

    CLOUD_PRODUCT_FORCED_REVOCATION_RESET_STATE(state: IAdminCloudProductsState): void {
      state.forcedRevocationError = undefined;
      state.forcedRevocationProgress = false;
    },

    CLOUD_PRODUCT_FORCED_REVOCATION_IN_PROGRESS(state: IAdminCloudProductsState): void {
      state.forcedRevocationError = undefined;
      state.forcedRevocationProgress = true;
    },

    CLOUD_PRODUCT_FORCED_REVOCATION_SUCCESS(state: IAdminCloudProductsState): void {
      state.forcedRevocationError = undefined;
      state.forcedRevocationProgress = false;
    },

    CLOUD_PRODUCT_FORCED_REVOCATION_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.forcedRevocationError = getErrorMessage(error);
      state.forcedRevocationProgress = false;
    },

    CLOUD_PRODUCT_RESET_FORCED_REVOCATION_RESET_STATE(state: IAdminCloudProductsState): void {
      state.resetForcedRevocationError = undefined;
      state.resetForcedRevocationProgress = false;
    },

    CLOUD_PRODUCT_RESET_FORCED_REVOCATION_IN_PROGRESS(state: IAdminCloudProductsState): void {
      state.resetForcedRevocationError = undefined;
      state.resetForcedRevocationProgress = true;
    },

    CLOUD_PRODUCT_RESET_FORCED_REVOCATION_SUCCESS(state: IAdminCloudProductsState): void {
      state.resetForcedRevocationError = undefined;
      state.resetForcedRevocationProgress = false;
    },

    CLOUD_PRODUCT_RESET_FORCED_REVOCATION_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.resetForcedRevocationError = getErrorMessage(error);
      state.resetForcedRevocationProgress = false;
    },

    REPLACE_STUCK_ENTITLEMENT_IN_PROGRESS(state: IAdminCloudProductsState): void {
      state.replaceStuckEntitlementError = undefined;
      state.replaceStuckEntitlementInProgress = true;
    },

    REPLACE_STUCK_ENTITLEMENT_SUCCESS(state: IAdminCloudProductsState): void {
      state.replaceStuckEntitlementError = undefined;
      state.replaceStuckEntitlementInProgress = false;
    },

    REPLACE_STUCK_ENTITLEMENT_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.replaceStuckEntitlementError = getErrorMessage(error);
      state.replaceStuckEntitlementInProgress = false;
    },

    CLOUD_PRODUCT_CHANGE_CREATION_DATE_SUCCESS(state: IAdminCloudProductsState): void {
      state.changeExpiryDateError = undefined;
      state.changeExpiryDateInProgress = false;
    },

    CLOUD_PRODUCT_CHANGE_CREATION_DATE_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.changeExpiryDateError = getErrorMessage(error);
      state.changeExpiryDateInProgress = false;
    },

    ORGANIZATION_CLOUD_PRODUCTS_LOADING(state: IAdminCloudProductsState): void {
      state.organizationAndUserCloudProductsLoadError = undefined;
      state.organizationAndUserCloudProductsLoading = true;
    },

    ORGANIZATION_CLOUD_PRODUCTS_LOADED(state: IAdminCloudProductsState, payload: CloudProductInfoDto[]): void {
      state.organizationAndUserCloudProducts = payload;
      state.organizationAndUserCloudProductsLoading = false;
    },

    ORGANIZATION_CLOUD_PRODUCTS_LOAD_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.organizationAndUserCloudProductsLoadError = getErrorMessage(error);
      state.organizationAndUserCloudProductsLoading = false;
    },

    USER_CLOUD_PRODUCTS_LOADING(state: IAdminCloudProductsState): void {
      state.userCloudProductsLoadError = undefined;
      state.userCloudProductsLoading = true;
    },

    USER_CLOUD_PRODUCTS_LOADED(state: IAdminCloudProductsState, payload: CloudProductInfoDto[]): void {
      state.organizationAndUserCloudProducts = payload;
      state.userCloudProductsLoading = false;
    },

    USER_CLOUD_PRODUCTS_LOAD_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.userCloudProductsLoadError = getErrorMessage(error);
      state.userCloudProductsLoading = false;
    },

    COMPUTERS_LOADING(state: IAdminCloudProductsState): void {
      state.computers = [];
      state.computersLoadError = undefined;
      state.computersLoading = true;
    },

    COMPUTERS_LOADED(state: IAdminCloudProductsState, computers: ComputerDto[]): void {
      state.computers = computers;
      state.computersLoading = false;
    },

    COMPUTERS_LOAD_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.computersLoading = false;
      state.computersLoadError = getErrorMessage(error);
    },

    CLOUD_ACTIVITIES_LOADING(state: IAdminCloudProductsState): void {
      state.cloudActivities = [];
      state.cloudActivitiesLoadError = undefined;
      state.cloudActivitiesLoading = true;
    },

    CLOUD_ACTIVITIES_LOADED(state: IAdminCloudProductsState, cloudActivities: GetCloudActivitiesResponseDto[]): void {
      state.cloudActivities = cloudActivities;
      state.cloudActivitiesLoading = false;
    },

    CLOUD_ACTIVITIES_LOAD_ERROR(state: IAdminCloudProductsState, error: Error): void {
      state.cloudActivitiesLoading = false;
      state.cloudActivitiesLoadError = getErrorMessage(error);
    },

    CLEAR_ORGANIZATION_CLOUD_PRODUCTS(state: IAdminCloudProductsState): void {
      state.cloudProducts = [];
      state.computers = [];
    },

    RESET_STATE(state: IAdminCloudProductsState): void {
      Object.assign(state, initialState());
    },
  },
  actions: {
    ...tableParams.actions,

    async loadCloudProducts({ commit, rootGetters }: ActionParams, searchParam: ISearchParams): Promise<void> {
      const { limit, offset, searchText, sortBy, sortDesc, includeOrganizationId } = searchParam || {};
      const isEduMode = rootGetters['adminEdu/isEduMode'];
      commit('CLOUD_PRODUCTS_LOADING', {
        limit: searchParam.limit,
        offset: searchParam.offset,
        searchText: searchParam.searchText,
      });
      try {
        const response = await getCloudApi(rootGetters['login/accessToken']).cloudControllerGetAllCloudProducts(
          searchParam.limit,
          searchParam.offset,
          searchParam.searchText,
          searchParam.sortBy,
          searchParam.sortDesc,
          includeOrganizationId,
          isEduMode,
        );
        commit('CLOUD_PRODUCTS_LOADED', response.data);
      } catch (error) {
        commit('CLOUD_PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('loadCloudProducts', error, { limit, offset, searchText, sortBy, sortDesc });
      }
    },

    async loadVisibleCloudProducts(
      { dispatch, rootGetters }: ActionParams,
      fromPage: 'organizations' | 'user',
    ): Promise<void> {
      if (fromPage === 'organizations') {
        const organizationId = rootGetters['adminOrganizations/currentOrganizationId'];
        await dispatch('loadOrganizationCloudProducts', organizationId);
      } else if (fromPage === 'user') {
        const userId = rootGetters['adminUsers/editingUserId'];
        await dispatch('loadUserCloudProducts', userId);
      }
    },

    async loadOrganizationCloudProducts({ commit, rootGetters }: ActionParams, organizationId: number): Promise<void> {
      try {
        commit('ORGANIZATION_CLOUD_PRODUCTS_LOADING');
        const response = await getCloudApi(rootGetters['login/accessToken']).cloudControllerFindAllByOrganization(
          organizationId,
        );
        commit('ORGANIZATION_CLOUD_PRODUCTS_LOADED', response.data);
      } catch (error) {
        commit('ORGANIZATION_CLOUD_PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('loadOrganizationCloudProducts', error, { organizationId });
      }
    },

    async loadUserCloudProducts({ commit, rootGetters }: ActionParams, userId: number): Promise<void> {
      try {
        commit('USER_CLOUD_PRODUCTS_LOADING');
        const response = await getCloudApi(rootGetters['login/accessToken']).cloudControllerFindAllByUser(userId);
        commit('USER_CLOUD_PRODUCTS_LOADED', response.data);
      } catch (error) {
        commit('USER_CLOUD_PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('loadUserCloudProducts', error, { userId });
      }
    },

    async assignCloudProduct(
      { commit, dispatch, rootGetters }: ActionParams,
      {
        cloudProductId,
        userId,
        fromPage,
      }: { cloudProductId: number; userId: number; fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('CLOUD_PRODUCT_ASSIGN_IN_PROGRESS');
        await getCloudApi(accessToken).cloudControllerAssignCloudProduct({ cloudProductId, userId });
        commit('CLOUD_PRODUCT_ASSIGN_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('CLOUD_PRODUCT_ASSIGN_ERROR', error);
        logger.captureStoreError('assignCloudProduct', error, { cloudProductId, userId, fromPage });
      }
    },

    async unAssignCloudProduct(
      { commit, dispatch, rootGetters }: ActionParams,
      { cloudProductId, fromPage }: { cloudProductId: number; fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('CLOUD_PRODUCT_ASSIGN_IN_PROGRESS');
        await getCloudApi(accessToken).cloudControllerUnAssignCloudProduct({ cloudProductId });
        commit('CLOUD_PRODUCT_ASSIGN_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('CLOUD_PRODUCT_ASSIGN_ERROR', error);
        logger.captureStoreError('unAssignCloudProduct', error, { cloudProductId, fromPage });
      }
    },

    async updateCloudProductNotes(
      { commit, dispatch, rootGetters }: ActionParams,
      { cloudProductId, notes, fromPage }: { cloudProductId: number; notes: string; fromPage: string },
    ): Promise<void> {
      commit('CLOUD_PRODUCT_CHANGE_NOTES_IN_PROGRESS');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerUpdateCloudProductNotes({
          cloudProductId,
          notes,
        });
        commit('CLOUD_PRODUCT_CHANGE_NOTES_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
      } catch (error) {
        commit('CLOUD_PRODUCT_CHANGE_NOTES_ERROR', error);
        logger.captureStoreError('updateDongleNotes', error, { cloudProductId, notes, fromPage });
      }
    },

    async changeExpiryDate(
      { commit, dispatch, rootGetters }: ActionParams,
      { cloudProductId, expiryDate, reason, fromPage }: ChangeExpiryDateDto & { fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      commit('CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_IN_PROGRESS');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerChangeExpiryDate({
          cloudProductId,
          expiryDate,
          reason,
        });
        commit('CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_ERROR', error);
        logger.captureStoreError('changeExpiryDate', error);
      }
    },

    async changeCreationDate(
      { commit, dispatch, rootGetters }: ActionParams,
      { cloudProductId, expiryDate, fromPage }: ChangeExpiryDateDto & { fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      commit('CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_IN_PROGRESS');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerChangeCreationDate({
          cloudProductId,
          creationDate: expiryDate,
        });
        commit('CLOUD_PRODUCT_CHANGE_CREATION_DATE_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('CLOUD_PRODUCT_CHANGE_CREATION_DATE_ERROR', error);
        logger.captureStoreError('changeCreationDate', error);
      }
    },

    async changeRenewalDate(
      { commit, dispatch, rootGetters }: ActionParams,
      { cloudProductId, expiryDate, fromPage }: ChangeExpiryDateDto & { fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      commit('CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_IN_PROGRESS');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerChangeRenewalDate({
          cloudProductId,
          renewalDate: expiryDate,
        });
        commit('CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('CLOUD_PRODUCT_CHANGE_EXPIRY_DATE_ERROR', error);
        logger.captureStoreError('changeRenewalDate', error);
      }
    },

    async forceRevocation(
      { commit, dispatch, rootGetters }: ActionParams,
      {
        cloudProductId,
        licenseCode,
        reason,
        fromPage,
      }: CpOnlineCancelForceRequestDto & { fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      commit('CLOUD_PRODUCT_FORCED_REVOCATION_IN_PROGRESS');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerCpOnlineCancelForce({
          cloudProductId,
          licenseCode,
          reason,
        });
        commit('CLOUD_PRODUCT_FORCED_REVOCATION_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('CLOUD_PRODUCT_FORCED_REVOCATION_ERROR', error);
        logger.captureStoreError('forcedRevocation', error);
      }
    },

    async resetForcedRevocation(
      { commit, dispatch, rootGetters }: ActionParams,
      {
        cloudProductId,
        licenseCode,
        reason,
        fromPage,
      }: CpResetOnlineCancelForceRequestDto & { fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      commit('CLOUD_PRODUCT_RESET_FORCED_REVOCATION_IN_PROGRESS');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerResetForcedRevocationCounter({
          cloudProductId,
          licenseCode,
          reason,
        });
        commit('CLOUD_PRODUCT_RESET_FORCED_REVOCATION_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('CLOUD_PRODUCT_RESET_FORCED_REVOCATION_ERROR', error);
        logger.captureStoreError('resetForcedRevocation', error);
      }
    },

    async replaceStuckEntitlement(
      { commit, dispatch, rootGetters }: ActionParams,
      { cloudProductId, fromPage }: { cloudProductId: number; fromPage: 'organizations' | 'user' },
    ): Promise<void> {
      commit('REPLACE_STUCK_ENTITLEMENT_IN_PROGRESS');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerReplaceStuckEntitlement({ cloudProductId });
        commit('REPLACE_STUCK_ENTITLEMENT_SUCCESS');
        await dispatch('loadVisibleCloudProducts', fromPage);
        await dispatch('loadCloudActivities', cloudProductId);
      } catch (error) {
        commit('REPLACE_STUCK_ENTITLEMENT_ERROR', error);
        logger.captureStoreError('replaceStuckEntitlement', error, { cloudProductId, fromPage });
      }
    },

    async selectCloudProduct({ commit, dispatch }: ActionParams, cloudProduct: CloudProductInfoDto): Promise<void> {
      commit('SELECT_CLOUD_PRODUCT', cloudProduct);
      await dispatch('loadComputers', cloudProduct.cloudProductId);
      await dispatch('loadCloudActivities', cloudProduct.cloudProductId);
    },

    async loadComputers({ commit, rootGetters }: ActionParams, cloudProductId: number): Promise<void> {
      commit('COMPUTERS_LOADING');
      try {
        const response = await getCloudApi(rootGetters['login/accessToken']).cloudControllerFindComputersByCloudProduct(
          cloudProductId,
        );
        commit('COMPUTERS_LOADED', response.data);
      } catch (error) {
        commit('COMPUTERS_LOAD_ERROR', error);
        logger.captureStoreError('loadComputers', error, { cloudProductId });
      }
    },

    async loadCloudActivities({ commit, rootGetters }: ActionParams, cloudProductId: number): Promise<void> {
      commit('CLOUD_ACTIVITIES_LOADING');
      try {
        const response = await getCloudApi(rootGetters['login/accessToken']).cloudControllerGetClActivities(
          cloudProductId,
        );
        commit('CLOUD_ACTIVITIES_LOADED', response.data);
      } catch (error) {
        commit('CLOUD_ACTIVITIES_LOAD_ERROR', error);
        logger.captureStoreError('loadComputers', error, { cloudProductId });
      }
    },

    async dropRenewalDate(
      { commit, dispatch, rootGetters }: ActionParams,
      {
        cloudProductId,
        fromPage,
      }: {
        cloudProductId: number;
        fromPage: 'organizations' | 'user';
      },
    ): Promise<void> {
      commit('ORGANIZATION_CLOUD_PRODUCTS_LOADING');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerDropRenewalDate({
          cloudProductId,
        });
      } catch (error) {
        commit('ORGANIZATION_CLOUD_PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('dropRenewalDate', error);
      }
      await dispatch('loadVisibleCloudProducts', fromPage);
    },

    async dropAutoRenewDisableDate(
      { commit, dispatch, rootGetters }: ActionParams,
      {
        cloudProductId,
        fromPage,
      }: {
        cloudProductId: number;
        fromPage: 'organizations' | 'user';
      },
    ): Promise<void> {
      commit('ORGANIZATION_CLOUD_PRODUCTS_LOADING');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerDropAutoRenewDisableDate({
          cloudProductId,
        });
      } catch (error) {
        commit('ORGANIZATION_CLOUD_PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('dropAutoRenewDisableDate', error);
      }
      await dispatch('loadVisibleCloudProducts', fromPage);
    },

    async switchAutoRenew(
      { commit, dispatch, rootGetters }: ActionParams,
      {
        cloudProductId,
        autoRenew,
        fromPage,
      }: {
        cloudProductId: number;
        autoRenew: boolean;
        fromPage: 'organizations' | 'user';
      },
    ): Promise<void> {
      commit('ORGANIZATION_CLOUD_PRODUCTS_LOADING');
      try {
        await getCloudApi(rootGetters['login/accessToken']).cloudControllerChangeAutoRenew({
          cloudProductId,
          autoRenew,
        });
      } catch (error) {
        commit('ORGANIZATION_CLOUD_PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('switchAutoRenew', error);
      }
      await dispatch('loadVisibleCloudProducts', fromPage);
    },

    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
  },
  getters: {
    ...tableParams.getters,

    cloudProducts: (state: IAdminCloudProductsState): CloudProductSearchResponseDto[] => state.cloudProducts,
    cloudProductsLoading: (state: IAdminCloudProductsState): boolean => state.cloudProductsLoading,
    cloudProductsLoadError: (state: IAdminCloudProductsState): undefined | string => state.cloudProductsLoadError,
    organizationCloudProducts: (state: IAdminCloudProductsState): CloudProductInfoDto[] =>
      state.organizationAndUserCloudProducts,
    organizationCloudProductsLoading: (state: IAdminCloudProductsState): boolean =>
      state.organizationAndUserCloudProductsLoading,
    organizationCloudProductsLoadError: (state: IAdminCloudProductsState): undefined | string =>
      state.organizationAndUserCloudProductsLoadError,
    userCloudProducts: (state: IAdminCloudProductsState): CloudProductInfoDto[] =>
      state.organizationAndUserCloudProducts,
    userCloudProductsLoading: (state: IAdminCloudProductsState): boolean => state.userCloudProductsLoading,
    userCloudProductsLoadError: (state: IAdminCloudProductsState): undefined | string =>
      state.userCloudProductsLoadError,
    computers: (state: IAdminCloudProductsState): ComputerDto[] => state.computers,
    computersLoading: (state: IAdminCloudProductsState): boolean => state.computersLoading,
    computersLoadError: (state: IAdminCloudProductsState): undefined | string => state.computersLoadError,
    assignInProgress: (state: IAdminCloudProductsState): boolean => state.assignInProgress,
    assignError: (state: IAdminCloudProductsState): undefined | string => state.assignError,
    changeNotesInProgress: (state: IAdminCloudProductsState): boolean => state.changeNotesInProgress,
    changeNotesError: (state: IAdminCloudProductsState): undefined | string => state.changeNotesError,
    changeExpiryDateInProgress: (state: IAdminCloudProductsState): boolean => state.changeExpiryDateInProgress,
    changeExpiryDateError: (state: IAdminCloudProductsState): undefined | string => state.changeExpiryDateError,
    forcedRevocationError: (state: IAdminCloudProductsState): undefined | string => state.forcedRevocationError,
    resetForcedRevocationError: (state: IAdminCloudProductsState): undefined | string =>
      state.resetForcedRevocationError,
    replaceStuckEntitlementInProgress: (state: IAdminCloudProductsState): boolean =>
      state.replaceStuckEntitlementInProgress,
    replaceStuckEntitlementError: (state: IAdminCloudProductsState): undefined | string =>
      state.replaceStuckEntitlementError,
    currentCloudProduct: (state: IAdminCloudProductsState): undefined | CloudProductInfoDto =>
      state.currentCloudProduct,
    currentCloudProductId: (state: IAdminCloudProductsState): undefined | number =>
      state.currentCloudProduct?.cloudProductId || undefined,
    cloudActivities: (state: IAdminCloudProductsState): GetCloudActivitiesResponseDto[] => state.cloudActivities,
    cloudActivitiesLoading: (state: IAdminCloudProductsState): boolean => state.cloudActivitiesLoading,
    cloudActivitiesLoadError: (state: IAdminCloudProductsState): undefined | string => state.cloudActivitiesLoadError,
  },
};
